import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import TeachingTooltip from '@/components/TeachingTooltip';
import { useTheme } from '@/context/ThemeContext';

const SQRT3 = Math.sqrt(3);

// Simple SVG power triangle component
const PowerTriangle = ({ P, Q, S, phi_deg, dark }) => {
  const isLeading = Q < 0;
  const absQ = Math.abs(Q);
  const maxW = 200, maxH = 110;
  const scale = P > 0 ? Math.min(maxW / P, (absQ > 0 ? maxH / absQ : maxH)) : 1;
  const ox = 30, oy = isLeading ? 30 : 30 + absQ * scale;
  const ax = ox + P * scale, ay = oy;
  const bx = ax, by = isLeading ? oy + absQ * scale : oy - absQ * scale;
  const svgH = Math.max(absQ * scale + 60, 80);
  const svgW = P * scale + 80;

  const textC = dark ? '#94a3b8' : '#64748b';
  const strokeP = '#0d9488', strokeQ = absQ > 0 ? (isLeading ? '#6366f1' : '#f59e0b') : '#94a3b8', strokeS = '#8b5cf6';

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full" style={{ maxHeight: 160 }}>
      {/* P — base, horizontal */}
      <line x1={ox} y1={oy} x2={ax} y2={ay} stroke={strokeP} strokeWidth="3" strokeLinecap="round" />
      <text x={(ox + ax) / 2} y={oy + 14} textAnchor="middle" fontSize="10" fill={strokeP} fontFamily="IBM Plex Mono">
        P = {P.toFixed(3)} MW
      </text>

      {/* Q — vertical */}
      {absQ > 0 && (
        <>
          <line x1={ax} y1={ay} x2={bx} y2={by} stroke={strokeQ} strokeWidth="3" strokeLinecap="round" />
          <text x={bx + 8} y={(ay + by) / 2 + 4} fontSize="10" fill={strokeQ} fontFamily="IBM Plex Mono" textAnchor="start">
            Q = {Q.toFixed(3)} MVAr
          </text>
        </>
      )}

      {/* S — hypotenuse */}
      <line x1={ox} y1={oy} x2={bx} y2={by} stroke={strokeS} strokeWidth="2.5" strokeDasharray="6 3" strokeLinecap="round" />
      <text
        x={(ox + bx) / 2 - 8}
        y={(oy + by) / 2 + 4}
        textAnchor="end" fontSize="10" fill={strokeS} fontFamily="IBM Plex Mono"
      >
        S = {S.toFixed(3)} MVA
      </text>

      {/* φ arc */}
      {phi_deg > 1 && (
        <>
          <path
            d={`M ${ox + 22} ${oy} A 22 22 0 0 ${isLeading ? 1 : 0} ${ox + 22 * Math.cos((isLeading ? 1 : -1) * phi_deg * Math.PI / 180)} ${oy + (isLeading ? 1 : -1) * 22 * Math.sin(phi_deg * Math.PI / 180)}`}
            fill="none" stroke={strokeS} strokeWidth="1.5"
          />
          <text x={ox + 26} y={oy + (isLeading ? 14 : -6)} fontSize="9" fill={strokeS} fontFamily="IBM Plex Mono">
            φ={phi_deg.toFixed(1)}°
          </text>
        </>
      )}

      {/* Right angle mark at base end */}
      {absQ > 0 && (
        <path
          d={`M ${ax - 8} ${ay} L ${ax - 8} ${isLeading ? ay + 8 : ay - 8} L ${ax} ${isLeading ? ay + 8 : ay - 8}`}
          fill="none" stroke={textC} strokeWidth="1"
        />
      )}
    </svg>
  );
};

const ThreePhasePowerCalc = () => {
  const { dark } = useTheme();
  const [vll, setVll] = useState(13.8);
  const [il, setIl] = useState(209.2);
  const [pf, setPf] = useState(0.92);
  const [isLagging, setIsLagging] = useState(true);

  const calc = useMemo(() => {
    const S = (SQRT3 * vll * il) / 1000; // MVA  (V in kV, I in A → MW = kV*A/1000)
    const P = S * pf;
    const phi = Math.acos(Math.max(0, Math.min(1, pf))) * 180 / Math.PI;
    const Q = S * Math.sin(phi * Math.PI / 180) * (isLagging ? 1 : -1);
    return { S, P, Q, phi };
  }, [vll, il, pf, isLagging]);

  const axisColor = dark ? '#64748b' : '#94a3b8';
  const gridColor = dark ? '#1e293b' : '#f1f5f9';
  const tooltipBg = dark ? '#1e293b' : '#ffffff';
  const tooltipBorder = dark ? '#334155' : '#e2e8f0';

  const chartData = [
    { name: 'P (MW)',   val: parseFloat(calc.P.toFixed(4)), color: '#0d9488' },
    { name: 'Q (MVAr)', val: parseFloat(Math.abs(calc.Q).toFixed(4)), color: isLagging ? '#f59e0b' : '#6366f1' },
    { name: 'S (MVA)',  val: parseFloat(calc.S.toFixed(4)), color: '#8b5cf6' },
  ];

  return (
    <div data-testid="three-phase-power-calc" className="space-y-5">
      {/* Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">V<sub>LL</sub> (kV)</label>
            <TeachingTooltip
              showIcon
              whatThis="The line-to-line (phase-to-phase) RMS voltage of the three-phase system. Distribution systems are specified by their line-to-line voltage: 13.8 kV, 25 kV, 69 kV, etc."
              tryThis="Change from 13.8 kV to 4.16 kV and watch S drop by 3× — apparent power is proportional to voltage at fixed current."
              whyMatters="All three-phase power calculations use V_LL, not V_LN. Using V_LN directly overstates apparent power by √3."
              connectedTo="→ Updates S = √3 × V_LL × I_L, P, Q, and both charts."
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="vll-power-input"
              type="range" min="0.1" max="765" step="0.1" value={vll}
              onChange={e => setVll(Number(e.target.value))}
              className="flex-1 accent-teal-600"
            />
            <input
              type="number" value={vll} step="0.1" min="0.1"
              onChange={e => setVll(Math.max(0.1, Number(e.target.value)))}
              className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none"
            />
            <span className="text-xs text-slate-500 w-7">kV</span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">I<sub>L</sub> (A)</label>
            <TeachingTooltip
              showIcon
              whatThis="The line current — the current flowing in each of the three phase conductors. Measured by CTs on the line side of a transformer or on the feeder conductors."
              tryThis="Set I_L to 418.4 A (the I_base for 100 MVA at 138 kV) and see how apparent power scales directly with current."
              whyMatters="Feeder conductor ampacity and transformer thermal rating are both expressed as maximum line current. Exceeding I_L_max causes overheating and insulation damage."
              connectedTo="→ Updates S = √3 × V_LL × I_L and all power outputs."
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="il-power-input"
              type="range" min="1" max="3000" step="1" value={il}
              onChange={e => setIl(Number(e.target.value))}
              className="flex-1 accent-teal-600"
            />
            <input
              type="number" value={il} step="1" min="0"
              onChange={e => setIl(Math.max(0, Number(e.target.value)))}
              className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none"
            />
            <span className="text-xs text-slate-500 w-5">A</span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Power Factor (pf = cos φ)
            </label>
            <TeachingTooltip
              showIcon
              whatThis="The ratio of real (useful) power to apparent power. pf = cos(φ) where φ is the angle between the voltage and current phasors. Range: 0 to 1.0."
              tryThis="Drag pf from 1.0 down to 0.7 and watch Q grow while P stays smaller — a lower pf means more reactive current for the same useful work."
              whyMatters="Low power factor increases line losses and requires oversized conductors and transformers. Utility tariffs penalise industrial customers with pf below 0.9 or 0.95."
              connectedTo="→ Updates P = S × pf, Q = S × sin(φ), and the power triangle visualization."
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="pf-input"
              type="range" min="0.01" max="1.0" step="0.001" value={pf}
              onChange={e => setPf(Number(e.target.value))}
              className="flex-1 accent-teal-600"
            />
            <input
              type="number" value={pf} step="0.001" min="0.01" max="1.0"
              onChange={e => setPf(Math.max(0.01, Math.min(1, Number(e.target.value))))}
              className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none"
            />
          </div>
          {/* Lagging/Leading toggle */}
          <div className="flex gap-2 mt-1">
            {[true, false].map(lag => (
              <button
                key={String(lag)}
                data-testid={lag ? 'lag-btn' : 'lead-btn'}
                onClick={() => setIsLagging(lag)}
                className={`text-xs px-2.5 py-1 rounded-md border font-medium transition-colors ${
                  isLagging === lag
                    ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 border-slate-900 dark:border-slate-100'
                    : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'
                }`}
              >
                {lag ? 'Lagging (ind.)' : 'Leading (cap.)'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'S (Apparent)', val: calc.S.toFixed(3), unit: 'MVA', color: 'purple' },
          { label: 'P (Real)', val: calc.P.toFixed(3), unit: 'MW', color: 'teal' },
          { label: 'Q (Reactive)', val: Math.abs(calc.Q).toFixed(3), unit: `MVAr ${isLagging ? 'lag' : 'lead'}`, color: isLagging ? 'amber' : 'indigo' },
          { label: 'φ (angle)', val: calc.phi.toFixed(2), unit: 'degrees', color: 'slate' },
        ].map(({ label, val, unit, color }) => (
          <div
            key={label}
            data-testid={`power-result-${label.split(' ')[0].toLowerCase()}`}
            className={`rounded-lg p-3 text-center border ${
              color === 'teal' ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800'
              : color === 'purple' ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
              : color === 'amber' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
              : color === 'indigo' ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800'
              : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
            }`}
          >
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">{label}</p>
            <p className="text-xl font-bold font-mono text-slate-900 dark:text-white">{val}</p>
            <p className="text-[10px] text-slate-500 dark:text-slate-500">{unit}</p>
          </div>
        ))}
      </div>

      {/* Power triangle + bar chart */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-500 mb-2">Power triangle</p>
          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
            <PowerTriangle P={calc.P} Q={calc.Q} S={calc.S} phi_deg={calc.phi} dark={dark} />
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-500 mb-2">P · Q · S comparison</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} margin={{ top: 14, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={{ stroke: axisColor }} />
              <Tooltip contentStyle={{ background: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 6, fontSize: 11 }} />
              <Bar dataKey="val" radius={[4, 4, 0, 0]}>
                {chartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                <LabelList dataKey="val" position="top" style={{ fontSize: 9, fill: axisColor }} formatter={v => v.toFixed(3)} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ThreePhasePowerCalc;
